var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { buildObjective } from "@/core/objectives";
import { currentUser } from "@/core/users/session";
import { HttpClient } from "@/lib/http";
import { ENDPOINTS } from "./config";
function normalizeObjectiveType(type, table) {
    // Compatibilidad con datos históricos mal tipados en objetivos_catalunya_rpv
    // (Volumen guardado como "volumen_catalunya" en lugar de "volumen_catalunya_rpv").
    if (table === "catalunya_rpv" && type === "volumen_catalunya") {
        return "volumen_catalunya_rpv";
    }
    return type;
}
const CANARIAS_OBJECTIVE_TYPES = new Set([
    "incremento_total_ventas_cu",
    "total_vidrio_retornable_mdo_local",
    "crecer_volumen_fuze_te",
    "crecer_clientes_compradores",
]);
// Mapeo de tipos de objetivos a sus configuraciones de campos
// Estos tipos deben coincidir exactamente con los definidos en:
// - app/Enums/ObjetivosCentroEnum.php
// - app/Enums/ObjetivosNorthEnum.php
const OBJECTIVE_TYPE_FIELD_MAPS = {
    // === NORTH REGION ===
    // Clientes (clientes_productivos en enum)
    clientes_productivos: {
        periodo_actual: "columnas_seleccionadas.cliente_esp_aact",
        periodo_anterior: "columnas_seleccionadas.clientes_esp_aant",
        objetivo_porcentaje: "columnas_seleccionadas.propuesta_objetivos_1rkpi",
    },
    // Volumen (cf_vidrio_retornable en enum)
    cf_vidrio_retornable: {
        periodo_actual: "columnas_seleccionadas.vol_esp_aact",
        periodo_anterior: "columnas_seleccionadas.vol_esp_aant",
        objetivo_porcentaje: "columnas_seleccionadas.propuesta_2nkpi",
    },
    // Cajas (canon_2025 en enum)
    canon_2025: {
        periodo_actual: "columnas_seleccionadas.cajas_aact",
        periodo_anterior: "columnas_seleccionadas.cajas_aant",
    },
    // Rutas CFL: Clientes Fuze Mantener (rutas_cfl_clientes_fuze_mantener en enum)
    rutas_cfl_clientes_fuze_mantener: {
        periodo_actual: "columnas_seleccionadas.clientes_esp_1_aact",
        periodo_anterior: "columnas_seleccionadas.clientes_esp_1_aant",
    },
    // === CENTER REGION ===
    // Volumen
    volumen: {
        periodo_actual: "columnas_seleccionadas.vol_mes_aact",
        periodo_anterior: "columnas_seleccionadas.vol_mes_aant",
        objetivo_porcentaje: "columnas_seleccionadas_center_volumen_castilla.crecimiento_aant",
        bp_cu_mes_natural: "columnas_seleccionadas_center_rutas_castilla.propuesta_bp_{mes_natural}",
    },
    // Volumen acumulado
    volumen_acumulado: {
        periodo_actual: "columnas_seleccionadas.vol_acum_aact",
        periodo_anterior: "columnas_seleccionadas.vol_acum_aant",
        objetivo_porcentaje: "columnas_seleccionadas_center_volumen_castilla.crec_acum",
        bp_cu_mes_natural: "columnas_seleccionadas_center_rutas_castilla.propuesta_bp_acumulado_{mes_natural}",
    },
    // Clientes
    clientes: {
        periodo_actual: "columnas_seleccionadas.clientes_aact",
        periodo_anterior: "columnas_seleccionadas.clientes_aant",
    },
    // Ref. Promedio
    ref_promedio: {
        periodo_actual: "columnas_seleccionadas.prom_refrx_cliente_aact",
        periodo_anterior: "columnas_seleccionadas.prom_refrx_cliente_aant",
    },
    // Cajas Regalo (Madrid)
    cajas_regalo: {
        venta_bruta_aact: "columnas_seleccionadas.venta_bruta_aact",
        venta_bruta_cajas_regalo: "columnas_seleccionadas.venta_bruta_cajas_regalo",
    },
    // GDA
    gda: {
        obj_ho_f1: "columnas_seleccionadas_gda_centers.obj_ho_f1",
        rtdo_ho_f1: "columnas_seleccionadas_gda_centers.rtdo_ho_f1",
        obj_ho_f2: "columnas_seleccionadas_gda_centers.obj_ho_f2",
        rtdo_ho_f2: "columnas_seleccionadas_gda_centers.rtdo_ho_f2",
        obj_ho_f3: "columnas_seleccionadas_gda_centers.obj_ho_f3",
        rtdo_ho_f3: "columnas_seleccionadas_gda_centers.rtdo_ho_f3",
        obj_al_f1: "columnas_seleccionadas_gda_centers.obj_al_f1",
        rtdo_al_f1: "columnas_seleccionadas_gda_centers.rtdo_al_f1",
        obj_al_f2: "columnas_seleccionadas_gda_centers.obj_al_f2",
        rtdo_al_f2: "columnas_seleccionadas_gda_centers.rtdo_al_f2",
        obj_al_f3: "columnas_seleccionadas_gda_centers.obj_al_f3",
        rtdo_al_f3: "columnas_seleccionadas_gda_centers.rtdo_al_f3",
    },
    // === EAST REGION (Baleares) ===
    comprad: {
        vol_aant: "columnas_seleccionadas.vol_aant",
        vol_aact: "columnas_seleccionadas.vol_aact",
    },
    f_rent_vta: {
        clientes_aant: "columnas_seleccionadas.clientes_aant",
        clientes_aact: "columnas_seleccionadas.clientes_aact",
    },
    // === CATALUNYA REGION ===
    // Volumen Catalunya
    volumen_catalunya: {
        periodo_actual: "columnas_seleccionadas.vol_esp_aact",
        periodo_anterior: "columnas_seleccionadas.vol_esp_aant",
        objetivo_porcentaje: "columnas_seleccionadas.propuesta_1rkpi",
    },
    // Clientes Catalunya
    clientes_catalunya: {
        periodo_actual: "columnas_seleccionadas.clientes_esp_aact",
        periodo_anterior: "columnas_seleccionadas.clientes_esp_aant",
        objetivo_porcentaje: "columnas_seleccionadas.propuesta_2nkpi",
    },
    // Cilindros Refrescos VR Catalunya
    cilindros_refrescos_vr_catalunya: {
        periodo_actual: "columnas_seleccionadas.clientes_esp_1_aact",
        periodo_anterior: "columnas_seleccionadas.clientes_esp_1_aant",
        objetivo_porcentaje: "columnas_seleccionadas.propuesta_3rkpi",
    },
    // === CATALUNYA RPV REGION ===
    // Volumen catalunya rpv
    volumen_catalunya_rpv: {
        periodo_actual: "columnas_seleccionadas.vol_aact",
        periodo_anterior: "columnas_seleccionadas.vol_aant",
        objetivo_porcentaje: "columnas_seleccionadas.propuesta_objetivos_1rkpi",
    },
    // Compradores catalunya rpv
    compradores_catalunya_rpv: {
        periodo_actual: "columnas_seleccionadas.clientes_aact",
        periodo_anterior: "columnas_seleccionadas.clientes_aant",
        objetivo_porcentaje: "columnas_seleccionadas.propuesta_objetivos_2nkpi",
    },
    ref_prom_catalunya_rpv: {
        periodo_actual: "columnas_seleccionadas.prom_refrx_cliente_aact",
        periodo_anterior: "columnas_seleccionadas.prom_refrx_cliente_aant",
        objetivo_porcentaje: "columnas_seleccionadas.propuesta_objetivos_3rkpi",
    },
    volumen_esp_catalunya_rpv: {
        vol_aact: "columnas_seleccionadas.vol_esp_aact",
        vol_aant: "columnas_seleccionadas.vol_esp_aant",
        objetivo_porcentaje: "columnas_seleccionadas.propuesta_objetivos_4rkpi",
    },
    clientes_esp_catalunya_rpv: {
        clientes_aact: "columnas_seleccionadas.clientes_esp_aact",
        clientes_aant: "columnas_seleccionadas.clientes_esp_aant",
        objetivo_porcentaje: "columnas_seleccionadas.propuesta_objetivos_5kpi",
    },
    // === BALEARES REGION ===
    volumen_total_distribucion_baleares: {
        periodo_actual: "columnas_seleccionadas.vol_aact",
        periodo_anterior: "columnas_seleccionadas.vol_aant",
        objetivo_porcentaje: "columnas_seleccionadas.1_kpi",
    },
    clientes_total_distribucion_baleares: {
        periodo_anterior: "columnas_seleccionadas.clientes_aant",
        periodo_actual: "columnas_seleccionadas.clientes_aact",
        objetivo_absoluto: "columnas_seleccionadas.2_kpi",
    },
    volumen_total_vitrio_retornable_baleares: {
        periodo_actual: "columnas_seleccionadas.vol_esp_aact",
        periodo_anterior: "columnas_seleccionadas.vol_esp_aant",
        objetivo_porcentaje: "columnas_seleccionadas.3_kpi",
    },
    clientes_total_vitrio_retornable_baleares: {
        periodo_anterior: "columnas_seleccionadas.clientes_esp_aant",
        periodo_actual: "columnas_seleccionadas.clientes_esp_aact",
        objetivo_absoluto: "columnas_seleccionadas.4_kpi",
    },
    variable_kpi_esp_baleares: {
        result_field: "columnas_seleccionadas.vol_esp_1_aact",
        objetivo_target: "columnas_seleccionadas.5_kpi",
    },
    // === CANARIAS REGION ===
    incremento_total_ventas_cu: {
        periodo_actual: "columnas_seleccionadas.vol_aact",
        periodo_anterior: "columnas_seleccionadas.vol_aant",
        objetivo_porcentaje: "columnas_seleccionadas.propuesta_1nkpi",
    },
    total_vidrio_retornable_mdo_local: {
        periodo_actual: "columnas_seleccionadas.vol_esp_aact",
        periodo_anterior: "columnas_seleccionadas.vol_esp_aant",
        objetivo_porcentaje: "columnas_seleccionadas.propuesta_2nkpi",
    },
    crecer_volumen_fuze_te: {
        periodo_actual: "columnas_seleccionadas.vol_esp_1_aact",
        periodo_anterior: "columnas_seleccionadas.vol_esp_1_aant",
        objetivo_porcentaje: "columnas_seleccionadas.propuesta_3nkpi",
    },
    crecer_clientes_compradores: {
        periodo_actual: "columnas_seleccionadas.clientes_aact",
        periodo_anterior: "columnas_seleccionadas.clientes_aant",
        objetivo_porcentaje: "columnas_seleccionadas.propuesta_4kpi",
    },
};
const CENTER_MADRID_OBJECTIVE_TYPE_FIELD_MAPS = {
    volumen: {
        periodo_actual: "columnas_seleccionadas.vol_mes_aact",
        periodo_anterior: "columnas_seleccionadas.vol_mes_aant",
    },
    volumen_acumulado: {
        periodo_actual: "columnas_seleccionadas.vol_acum_aact",
        periodo_anterior: "columnas_seleccionadas.vol_acum_aant",
    },
    clientes: {
        periodo_actual: "columnas_seleccionadas.clientes_aact",
        periodo_anterior: "columnas_seleccionadas.clientes_aant",
    },
    gda: {
        obj_ho_f1: "columnas_seleccionadas_gda_centers.obj_ho_f1",
        rtdo_ho_f1: "columnas_seleccionadas_gda_centers.rtdo_ho_f1",
        obj_ho_f2: "columnas_seleccionadas_gda_centers.obj_ho_f2",
        rtdo_ho_f2: "columnas_seleccionadas_gda_centers.rtdo_ho_f2",
        obj_ho_f3: "columnas_seleccionadas_gda_centers.obj_ho_f3",
        rtdo_ho_f3: "columnas_seleccionadas_gda_centers.rtdo_ho_f3",
        obj_al_f1: "columnas_seleccionadas_gda_centers.obj_al_f1",
        rtdo_al_f1: "columnas_seleccionadas_gda_centers.rtdo_al_f1",
        obj_al_f2: "columnas_seleccionadas_gda_centers.obj_al_f2",
        rtdo_al_f2: "columnas_seleccionadas_gda_centers.rtdo_al_f2",
        obj_al_f3: "columnas_seleccionadas_gda_centers.obj_al_f3",
        rtdo_al_f3: "columnas_seleccionadas_gda_centers.rtdo_al_f3",
    },
    cajas_regalo: {
        venta_bruta_cajas_regalo: "columnas_seleccionadas.venta_bruta_cajas_regalo",
        venta_bruta_aact: "columnas_seleccionadas.venta_bruta_aact",
    },
    ref_promedio: {
        periodo_actual: "columnas_seleccionadas.prom_refrx_cliente_aact",
        periodo_anterior: "columnas_seleccionadas.prom_refrx_cliente_aant",
    },
};
function buildCatalunyaThirdKpiFieldMap() {
    return {
        periodo_actual: "columnas_seleccionadas.periodo_actual",
        periodo_anterior: "columnas_seleccionadas.periodo_anterior",
        objetivo_porcentaje: "columnas_seleccionadas.propuesta_3rkpi",
    };
}
function buildCanariasIncrementoVentasFieldMap(activeColumns) {
    const usesClientesColumns = activeColumns.includes("clientes_aact") || activeColumns.includes("clientes_aant");
    return {
        periodo_actual: usesClientesColumns
            ? "columnas_seleccionadas.clientes_aact"
            : "columnas_seleccionadas.vol_aact",
        periodo_anterior: usesClientesColumns
            ? "columnas_seleccionadas.clientes_aant"
            : "columnas_seleccionadas.vol_aant",
        objetivo_porcentaje: "columnas_seleccionadas.propuesta_1nkpi",
    };
}
function buildCanariasVidrioRetornableFieldMap(activeColumns) {
    const usesClientesColumns = activeColumns.includes("clientes_esp_aact") || activeColumns.includes("clientes_esp_aant");
    return {
        periodo_actual: usesClientesColumns
            ? "columnas_seleccionadas.clientes_esp_aact"
            : "columnas_seleccionadas.vol_esp_aact",
        periodo_anterior: usesClientesColumns
            ? "columnas_seleccionadas.clientes_esp_aant"
            : "columnas_seleccionadas.vol_esp_aant",
        objetivo_porcentaje: "columnas_seleccionadas.propuesta_2nkpi",
    };
}
function buildCanariasFuzeFieldMap(activeColumns) {
    const usesClientesColumns = activeColumns.includes("clientes_esp_1_aact") || activeColumns.includes("clientes_esp_1_aant");
    return {
        periodo_actual: usesClientesColumns
            ? "columnas_seleccionadas.clientes_esp_1_aact"
            : "columnas_seleccionadas.vol_esp_1_aact",
        periodo_anterior: usesClientesColumns
            ? "columnas_seleccionadas.clientes_esp_1_aant"
            : "columnas_seleccionadas.vol_esp_1_aant",
        objetivo_porcentaje: "columnas_seleccionadas.propuesta_3nkpi",
    };
}
function buildCanariasClientesCompradoresFieldMap(activeColumns) {
    const usesVolumenColumns = activeColumns.includes("vol_aact") || activeColumns.includes("vol_aant") || activeColumns.includes("vol_esp_2_aact") || activeColumns.includes("vol_esp_2_aant");
    return {
        periodo_actual: usesVolumenColumns
            ? activeColumns.includes("vol_esp_2_aact") ? "columnas_seleccionadas.vol_esp_2_aact" : "columnas_seleccionadas.vol_aact"
            : activeColumns.includes("clientes_esp_2_aact") ? "columnas_seleccionadas.clientes_esp_2_aact" : "columnas_seleccionadas.clientes_aact",
        periodo_anterior: usesVolumenColumns
            ? activeColumns.includes("vol_esp_2_aant") ? "columnas_seleccionadas.vol_esp_2_aant" : "columnas_seleccionadas.vol_aant"
            : activeColumns.includes("clientes_esp_2_aant") ? "columnas_seleccionadas.clientes_esp_2_aant" : "columnas_seleccionadas.clientes_aant",
        objetivo_porcentaje: "columnas_seleccionadas.propuesta_4kpi",
    };
}
// Normalización de tipos: mapea tipos del backend a tipos de fórmulas
const TYPE_NORMALIZATION = {
    // Center
    volumen: "volumen2",
    volumen_acumulado: "volumen2",
    clientes: "clientes",
    ref_promedio: "ref_promedio",
    gda: "gda",
    // North
    clientes_productivos: "clientes_north",
    cf_vidrio_retornable: "volumen_north",
    canon_2025: "canon",
    rutas_cfl_clientes_fuze_mantener: "clientes_north_fuze", // Usa la misma fórmula que clientes
    // Center Madrid
    cajas_regalo: "cajas_regalo",
    // East
    //ibiza
    comprad: "comprad",
    f_rent_vta: "f_rent_vta",
    // Catalunya
    volumen_catalunya: "volumen_catalunya",
    clientes_catalunya: "clientes_catalunya",
    cilindros_refrescos_vr_catalunya: "cilindros_refrescos_vr_catalunya",
    // Canarias
    incremento_total_ventas_cu: "incremento_total_ventas_cu",
    total_vidrio_retornable_mdo_local: "total_vidrio_retornable_mdo_local",
    crecer_volumen_fuze_te: "crecer_volumen_fuze_te",
    crecer_clientes_compradores: "crecer_clientes_compradores",
    // South
    incremento_volumen_sur: "south_volumen_display_values",
    incremento_clientes_sur: "south_clientes_display_values",
    incremento_dn_sur: "south_dn_display_values",
};
const CENTER_MADRID_TYPE_NORMALIZATION = {
    volumen: "center_madrid_yoy",
    volumen_acumulado: "center_madrid_yoy",
    clientes: "center_madrid_yoy",
    gda: "gda",
    cajas_regalo: "cajas_regalo",
    ref_promedio: "center_madrid_ref_promedio",
};
export function parseObjectiveConfig(obj, table) {
    var _a;
    const type = normalizeObjectiveType(obj.type, table);
    const activeColumns = (_a = obj.activeColumns) !== null && _a !== void 0 ? _a : [];
    const isSouthObjective = type.startsWith("incremento_") && type.endsWith("_sur");
    const isCenterMadridObjective = !table && currentUser().region_code === "C100" && type in CENTER_MADRID_OBJECTIVE_TYPE_FIELD_MAPS;
    const fieldMap = isSouthObjective
        ? Object.fromEntries(activeColumns.map((column) => [column, `columnas_seleccionadas.${column}`]))
        : isCenterMadridObjective
            ? CENTER_MADRID_OBJECTIVE_TYPE_FIELD_MAPS[type]
            : type === "cilindros_refrescos_vr_catalunya"
                ? buildCatalunyaThirdKpiFieldMap()
                : type === "incremento_total_ventas_cu" && currentUser().region_code === "S300"
                    ? buildCanariasIncrementoVentasFieldMap(activeColumns)
                    : type === "total_vidrio_retornable_mdo_local" && currentUser().region_code === "S300"
                        ? buildCanariasVidrioRetornableFieldMap(activeColumns)
                        : type === "crecer_volumen_fuze_te" && currentUser().region_code === "S300"
                            ? buildCanariasFuzeFieldMap(activeColumns)
                            : type === "crecer_clientes_compradores" && currentUser().region_code === "S300"
                                ? buildCanariasClientesCompradoresFieldMap(activeColumns)
                                : OBJECTIVE_TYPE_FIELD_MAPS[type];
    if (!fieldMap) {
        throw new Error(`Unknown objective type: ${type}. Available types: ${Object.keys(OBJECTIVE_TYPE_FIELD_MAPS).join(", ")}`);
    }
    // Normalizar el tipo para usar con las fórmulas
    const normalizedType = isCenterMadridObjective
        ? CENTER_MADRID_TYPE_NORMALIZATION[type] || type
        : TYPE_NORMALIZATION[type] || type;
    // Canarias growth KPIs: override computed field labels to match results-report display
    let fieldLabels;
    if (currentUser().region_code === "S300") {
        const usesClientesSlot1 = activeColumns.includes("clientes_aact") || activeColumns.includes("clientes_aant");
        const usesClientesSlot2 = activeColumns.includes("clientes_esp_aact") || activeColumns.includes("clientes_esp_aant");
        const usesClientesSlot3 = activeColumns.includes("clientes_esp_1_aact") || activeColumns.includes("clientes_esp_1_aant");
        if (type === "incremento_total_ventas_cu") {
            fieldLabels = { objetivo_cu: "Objetivo", cajas_faltantes: usesClientesSlot1 ? "Clientes faltantes" : "Volumen faltante" };
        }
        else if (type === "total_vidrio_retornable_mdo_local") {
            fieldLabels = { objetivo_cu: "Objetivo", cajas_faltantes: usesClientesSlot2 ? "Clientes faltantes" : "Volumen faltante" };
        }
        else if (type === "crecer_volumen_fuze_te") {
            fieldLabels = { objetivo_cu: "Objetivo", cajas_faltantes: usesClientesSlot3 ? "Clientes faltantes" : "Volumen faltante" };
        }
    }
    return {
        id: obj.id,
        name: obj.name,
        type: normalizedType,
        map: fieldMap,
        sourceKeys: isSouthObjective ? activeColumns : undefined,
        color: obj.color,
        fieldLabels,
    };
}
export function fetchAssignedObjectives(table) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield HttpClient.get(ENDPOINTS.FETCH_ASSIGNED_OBJECTIVES, { table: table });
        return response.data
            .map((obj) => {
            try {
                return buildObjective(parseObjectiveConfig(obj, table));
            }
            catch (error) {
                console.warn("Skipping invalid objective config", { objective: obj, error });
                return null;
            }
        })
            .filter((objective) => objective !== null);
    });
}
/**
 * Fetch all available objectives for assignment
 */
export function fetchAllObjectives(table, period) {
    return __awaiter(this, void 0, void 0, function* () {
        const params = { table: table !== null && table !== void 0 ? table : "" };
        if (period) {
            params.ano_natural = String(period.year);
            params.mes_natural = String(period.month);
        }
        const response = yield HttpClient.get(ENDPOINTS.FETCH_ALL_OBJECTIVES, params);
        return response.data.map((obj) => {
            var _a;
            return ({
                id: obj.id,
                // Show the period-dependent title in the modal, fall back to the stable name.
                name: (_a = obj.display_name) !== null && _a !== void 0 ? _a : obj.name,
                type: obj.type,
                color: obj.color,
            });
        });
    });
}
/**
 * Sync objective assignments (assign new, unassign removed)
 */
export function syncObjectiveAssignments(objectiveIds, table) {
    return __awaiter(this, void 0, void 0, function* () {
        yield HttpClient.post(ENDPOINTS.SYNC_OBJECTIVE_ASSIGNMENTS, { objetivo_ids: objectiveIds, table: table });
    });
}
